import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  DoorOpen,
  History,
  KeyRound,
  LogIn,
  Lock,
  RotateCcw,
  ScrollText,
  Search,
  ShieldAlert,
  Trash2,
  UserPlus,
  Users
} from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import StatPill from '../../components/ui/StatPill';
import Badge from '../../components/ui/Badge';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Skeleton from '../../components/ui/Skeleton';
import Avatar from '../../components/ui/Avatar';
import { getAuditLog } from '../../utils/api';
import { formatDateTime, relativeTime } from '../../utils/commandCenterHelpers';

// type → { icon, tone, label }
const EVENT_META = {
  'auth.login.success': { icon: LogIn, tone: 'success', label: 'Sign in' },
  'auth.login.failed': { icon: ShieldAlert, tone: 'warning', label: 'Failed login' },
  'auth.login.blocked': { icon: Lock, tone: 'danger', label: 'Blocked login' },
  'auth.login.locked': { icon: Lock, tone: 'danger', label: 'Account locked' },
  'auth.password.change': { icon: KeyRound, tone: 'brand', label: 'Password change' },
  'user.create': { icon: UserPlus, tone: 'success', label: 'Account created' },
  'user.update': { icon: Users, tone: 'brand', label: 'Account updated' },
  'user.password.reset': { icon: KeyRound, tone: 'warning', label: 'Password reset' },
  'user.delete': { icon: Trash2, tone: 'danger', label: 'Account deleted' },
  'entity.rollback': { icon: RotateCcw, tone: 'warning', label: 'Rollback' },
  'entity.restore': { icon: History, tone: 'success', label: 'Restore' },
  'entity.purge': { icon: Trash2, tone: 'danger', label: 'Purge' },
  'export.gtfs': { icon: DoorOpen, tone: 'brand', label: 'GTFS export' }
};

function metaFor(type) {
  return EVENT_META[type] || { icon: ScrollText, tone: 'default', label: type };
}

const FILTERS = [
  { value: 'all', label: 'All events' },
  { value: 'auth', label: 'Authentication' },
  { value: 'user', label: 'Accounts' },
  { value: 'entity', label: 'Data changes' }
];

export default function AuditLogPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAuditLog({ limit: 300 });
      setEvents(res.data.events);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    let list = [...events];
    if (filter !== 'all') list = list.filter((e) => e.type.startsWith(filter));
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (e) =>
          (e.summary || '').toLowerCase().includes(q) ||
          (e.actor?.username || '').toLowerCase().includes(q) ||
          e.type.toLowerCase().includes(q)
      );
    }
    return list;
  }, [events, filter, search]);

  return (
    <>
      <PageHeader
        eyebrow="Super Admin"
        title="Audit log"
        subtitle="A chronological trail of sign-ins, account changes, rollbacks and exports."
        icon={ScrollText}
        pills={[<StatPill key="n" icon={History} label="Events" value={events.length} />]}
      />

      <div className="mt-5 rounded-2xl border border-slate-200 bg-white shadow-soft">
        <div className="px-5 py-3.5 border-b border-slate-100 flex flex-col sm:flex-row gap-2.5">
          <Input
            leftIcon={Search}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search summary, user or type"
            className="sm:flex-1"
          />
          <Select value={filter} onChange={(e) => setFilter(e.target.value)} className="sm:w-44">
            {FILTERS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </Select>
        </div>

        {loading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}
          </div>
        ) : visible.length === 0 ? (
          <div className="py-14 text-center">
            <p className="text-[14px] font-medium text-slate-700">No events</p>
            <p className="text-[12.5px] text-slate-500 mt-1">Nothing matches the current filters.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {visible.map((e) => {
              const meta = metaFor(e.type);
              const Icon = meta.icon;
              return (
                <li key={e.id} className="px-5 py-3 flex items-center gap-3">
                  <span
                    className={`h-9 w-9 rounded-xl inline-flex items-center justify-center shrink-0 ${toneBg(meta.tone)}`}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-[13px] font-medium text-slate-900 truncate">{e.summary || meta.label}</p>
                      <Badge variant={meta.tone === 'default' ? 'outline' : meta.tone} size="sm">{meta.label}</Badge>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 text-[11.5px] text-slate-400">
                      <Avatar name={e.actor?.name || e.actor?.username || 'System'} size="xs" />
                      <span>{e.actor?.name || e.actor?.username || 'System'}</span>
                      {e.actor?.role && <span>· {e.actor.role}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[12px] text-slate-500">{relativeTime(e.createdAt)}</p>
                    <p className="text-[11px] text-slate-400">{formatDateTime(e.createdAt)}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

function toneBg(tone) {
  return {
    success: 'bg-emerald-50 text-emerald-600',
    warning: 'bg-amber-50 text-amber-600',
    danger: 'bg-rose-50 text-rose-600',
    brand: 'bg-brand-50 text-brand-600',
    default: 'bg-slate-100 text-slate-500'
  }[tone];
}
